import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { Hint } from '../../components/ui/Hint';
import { num } from '../../format';
import { DuplicateWarning } from './DuplicateWarning';
import { AddManufacturer, AddPackage, AddSubtype } from './QuickAdd';
import { PackageEditor } from './PackageEditor';
import { useLabel } from './labels';
import {
  attachComponentImage,
  checkDuplicates,
  createComponent,
  listComponentImages,
  getType,
  listColors,
  listManufacturers,
  listPackages,
  listTypes,
  listUnits,
  updateComponent,
  updatePackageMeasurementProfile,
  type Attribute,
  type Component,
  type ComponentInput,
  type DuplicateReport,
  type MeasurementProfile,
} from './api';

interface Props {
  /** Present when editing, absent when creating. */
  component?: Component;
  /**
   * Prefill a *new* component from an existing one.
   *
   * The case this exists for: a shelf of resistors that differ only in value.
   * Retyping the type, package, tolerance and rating for each is how people end
   * up not recording the parameters at all.
   */
  copyFrom?: Component;
  onSaved: (component: Component) => void;
  onCancel: () => void;
  /** Opening a warned-about duplicate instead of creating a second one. */
  onOpenExisting: (componentId: string) => void;
  /**
   * Whether anything has been changed.
   *
   * Reported upwards so the list can switch components straight away when
   * nothing would be lost, and ask only when something would.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

type Values = Record<string, string>;

/** Non-breaking, because a select collapses ordinary leading spaces and the
 *  indent is the only thing showing where a subtype sits in the tree. */
const INDENT = '  ';

/**
 * The entry form, shaped by the chosen type.
 *
 * Two things happen while it is being filled in rather than when it is
 * submitted: the name is generated from the fields, and the duplicate check
 * runs. Both are deliberate — a warning that arrives after the work is done is a
 * warning people dismiss, and a name proposed at the end is a name they have
 * already replaced with their own.
 */
export function ComponentForm({
  component,
  copyFrom,
  onSaved,
  onCancel,
  onOpenExisting,
  onDirtyChange,
}: Props) {
  const { t } = useTranslation();
  const { pick } = useLabel();
  const queryClient = useQueryClient();

  const source = component ?? copyFrom;

  const [typeId, setTypeId] = useState(source?.component_type_id ?? '');
  const [typeSearch, setTypeSearch] = useState('');
  // A copy never inherits the part number: it is unique per manufacturer, and
  // the point of copying is that this is a different part.
  const [mpn, setMpn] = useState(component?.mpn ?? '');
  const [manufacturerId, setManufacturerId] = useState(source?.manufacturer_id ?? '');
  const [packageId, setPackageId] = useState(source?.package_id ?? '');
  const [colorId, setColorId] = useState(source?.color_id ?? '');
  const [value, setValue] = useState(source?.value_display ?? '');
  const [unitCode, setUnitCode] = useState(source?.unit_code ?? 'pcs');
  const [description, setDescription] = useState(source?.description ?? '');
  const [datasheetUrl, setDatasheetUrl] = useState(source?.datasheet_url ?? '');
  // Blank rather than "0": zero would put every part on the reorder list the
  // moment it runs out, and a list that includes everything is not read.
  const [minStock, setMinStock] = useState(num(source?.min_stock));
  const [trackSerials, setTrackSerials] = useState(source?.track_serials ?? false);
  const [measurementMode, setMeasurementMode] = useState<MeasurementProfile['mode'] | ''>(
    component?.measurement_profile?.mode ?? '',
  );
  const [sampleQuantity, setSampleQuantity] = useState(
    String(component?.measurement_profile?.sample_quantity ?? 10),
  );
  const [sampleWeight, setSampleWeight] = useState(component?.measurement_profile?.sample_weight_g ?? '');
  const [carrierWeight, setCarrierWeight] = useState(component?.measurement_profile?.carrier_weight_g ?? '0');
  const [tapeLength, setTapeLength] = useState(component?.measurement_profile?.tape_length_10_cells_mm ?? '');
  const [attributes, setAttributes] = useState<Values>(() => {
    const initial: Values = {};
    for (const [key, raw] of Object.entries(source?.attributes ?? {})) {
      initial[key] = raw === null || raw === undefined ? '' : String(raw);
    }
    return initial;
  });

  // Empty means "follow the generated name". Typing here pins it — the same
  // rule the server applies, mirrored so the field can show what will be saved.
  //
  // A copy deliberately starts unpinned: changing the value is the whole reason
  // for copying, and the name should follow it rather than keep the original's.
  const [name, setName] = useState(component?.name ?? '');
  const [nameTouched, setNameTouched] = useState(Boolean(component));
  const [packageScope, setPackageScope] = useState<'enabled' | 'all'>('enabled');
  const [colorScope, setColorScope] = useState<'enabled' | 'all'>('enabled');
  const [adding, setAdding] = useState<'maker' | 'package' | 'package-editor' | 'subtype' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Wrapped, not passed by reference: TanStack calls the function with its own
  // query context, which would land in the scope parameter.
  const types = useQuery({
    queryKey: ['reference', 'types', 'all'],
    queryFn: () => listTypes('all'),
  });
  const units = useQuery({ queryKey: ['reference', 'units'], queryFn: listUnits });
  const makers = useQuery({
    queryKey: ['reference', 'manufacturers'],
    queryFn: () => listManufacturers(),
  });
  const packages = useQuery({
    queryKey: ['reference', 'packages', packageScope],
    queryFn: () => listPackages(packageScope),
  });
  const colors = useQuery({
    queryKey: ['reference', 'colors', colorScope],
    queryFn: () => listColors(colorScope),
  });
  const typeDetail = useQuery({
    queryKey: ['reference', 'type', typeId],
    queryFn: () => getType(typeId),
    enabled: Boolean(typeId),
  });

  const specs: Attribute[] = useMemo(
    () => typeDetail.data?.attributes ?? [],
    [typeDetail.data],
  );

  const input: ComponentInput = useMemo(
    () => ({
      mpn: mpn || null,
      manufacturer_id: manufacturerId || null,
      component_type_id: typeId || null,
      package_id: packageId || null,
      color_id: colorId || null,
      value: value || null,
      unit_code: unitCode,
      description: description || null,
      datasheet_url: datasheetUrl || null,
      min_stock: minStock === '' ? null : minStock,
      track_serials: trackSerials,
      measurement_profile: measurementMode
        ? {
            mode: measurementMode,
            sample_quantity: Number(sampleQuantity),
            sample_weight_g: sampleWeight,
            carrier_weight_g: carrierWeight || '0',
            tape_length_10_cells_mm: measurementMode === 'tape' ? tapeLength : null,
          }
        : null,
      attributes: Object.fromEntries(
        Object.entries(attributes).filter(([, raw]) => raw !== ''),
      ),
    }),
    [
      mpn,
      manufacturerId,
      typeId,
      packageId,
      colorId,
      value,
      unitCode,
      description,
      datasheetUrl,
      minStock,
      trackSerials,
      measurementMode,
      sampleQuantity,
      sampleWeight,
      carrierWeight,
      tapeLength,
      attributes,
    ],
  );

  // A snapshot of the values the form opened with. Comparing against it is what
  // lets the list switch components silently when nothing was touched — the
  // common case, since most of the time the form is opened and then abandoned
  // in favour of a different part.
  const opened = useRef<string | null>(null);
  const snapshot = JSON.stringify([input, name, nameTouched]);
  if (opened.current === null) opened.current = snapshot;
  const dirty = opened.current !== snapshot;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Whatever the form was holding stops existing when the form does. Without
  // this the last reported state stayed "changed" after a successful save, and
  // the next component clicked asked about work that had already been written.
  const notify = useRef(onDirtyChange);
  notify.current = onDirtyChange;
  useEffect(() => () => notify.current?.(false), []);

  // Debounced so that typing a part number does not fire a request per
  // keystroke; 400 ms is long enough to finish a word and short enough that the
  // warning still lands before the next field.
  const [probe, setProbe] = useState<ComponentInput | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setProbe(input), 400);
    return () => clearTimeout(timer);
  }, [input]);

  const identifying = Boolean(typeId || mpn);
  const report = useQuery<DuplicateReport>({
    queryKey: ['components', 'check', probe],
    queryFn: () => checkDuplicates({ ...probe!, exclude_id: component?.id ?? null }),
    enabled: Boolean(probe) && identifying,
  });

  useEffect(() => {
    if (!nameTouched && report.data?.suggested_name) setName(report.data.suggested_name);
  }, [nameTouched, report.data?.suggested_name]);

  const save = useMutation({
    mutationFn: async (body: ComponentInput) => {
      if (component) return updateComponent(component.id, body);
      const created = await createComponent(body);
      // A duplicate looks the same, so it looks the same in the photograph.
      // Attaching the original's files costs nothing on disk: files are stored
      // by the hash of their content, so the same picture on fifty resistors is
      // one picture with fifty references.
      if (copyFrom) {
        const photos = await listComponentImages(copyFrom.id);
        for (const photo of photos) {
          await attachComponentImage(created.id, photo.file_id);
        }
      }
      return created;
    },
    onSuccess: (saved) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['components'] });
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
      // Clear the parent's guard before it changes the visible record. The
      // form unmounts immediately afterwards, but that cleanup is too late for
      // a click made in the same event turn.
      onDirtyChange?.(false);
      onSaved(saved);
    },
    onError: (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown'),
  });

  const savePackageProfile = useMutation({
    mutationFn: (profile: MeasurementProfile) => updatePackageMeasurementProfile(packageId, profile),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reference'] }),
  });

  const rootTypes = (types.data ?? []).filter((row) => row.parent_id === null);
  const childrenOf = (parentId: string) =>
    (types.data ?? []).filter((row) => row.parent_id === parentId);
  const typeNeedle = typeSearch.trim().toLocaleLowerCase();
  const matchesType = (type: { code: string; name_ru: string; name_en: string }) =>
    !typeNeedle ||
    [type.code, type.name_ru, type.name_en].some((value) =>
      value.toLocaleLowerCase().includes(typeNeedle),
    );
  const matchesBranch = (type: { id: string; code: string; name_ru: string; name_en: string }): boolean =>
    matchesType(type) || childrenOf(type.id).some(matchesBranch);

  // The same pair of buttons above and below. The form is long enough that
  // saving otherwise means scrolling past everything just typed.
  const actions = (
    <div className="component-form__actions">
      <button type="submit" disabled={save.isPending}>
        {t('common.save')}
      </button>
      <button type="button" onClick={onCancel}>
        {t('common.cancel')}
      </button>
    </div>
  );

  return (
    <form
      className="component-form"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate({ ...input, name: nameTouched ? name : null });
      }}
    >
      {actions}

      <div className="component-form__row">
        <label>
          {t('components.type')}
          <input
            value={typeSearch}
            onChange={(event) => setTypeSearch(event.target.value)}
            placeholder={t('components.typeSearch')}
            aria-label={t('components.typeSearch')}
          />
          <select
            value={typeId}
            onChange={(event) => {
              setTypeId(event.target.value);
              setTypeSearch('');
              // Parameters belong to the type. Keeping the old answers would
              // carry a resistor's tolerance onto a connector.
              setAttributes({});
            }}
          >
            <option value="">{t('components.typePlaceholder')}</option>
            {rootTypes.filter(matchesBranch).map((group) => (
              <optgroup key={group.id} label={pick(group.name_ru, group.name_en)}>
                <option value={group.id}>{pick(group.name_ru, group.name_en)}</option>
                {childrenOf(group.id).map((child) => (
                  <TypeOptions
                    key={child.id}
                    type={child}
                    childrenOf={childrenOf}
                    matchesBranch={matchesBranch}
                    depth={1}
                  />
                ))}
              </optgroup>
            ))}
          </select>
          {typeId && (
            <button
              type="button"
              className="component-form__scope"
              onClick={() => setAdding(adding === 'subtype' ? null : 'subtype')}
            >
              {t('components.addSubtypeShort')}
            </button>
          )}
        </label>

        {typeDetail.data?.value_kind && (
          <label>
            {t('components.value')}
            <input
              value={value}
              // Comma and point are one separator here. The parser has always
              // accepted both; showing the canonical form is what makes that
              // visible instead of something to be taken on faith.
              onChange={(event) => setValue(event.target.value.replace(',', '.'))}
              placeholder={t(`components.valueHint.${typeDetail.data.value_kind}`)}
            />
            {report.data?.value_display && (
              <span className="component-form__parsed">= {report.data.value_display}</span>
            )}
          </label>
        )}
      </div>

      <div className="component-form__row">
        <label>
          {t('components.mpn')}
          <input value={mpn} onChange={(event) => setMpn(event.target.value)} />
        </label>
        <label>
          {t('components.manufacturer')}
          <select
            value={manufacturerId}
            onChange={(event) => setManufacturerId(event.target.value)}
          >
            <option value="">—</option>
            {(makers.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="component-form__scope"
            onClick={() => setAdding(adding === 'maker' ? null : 'maker')}
          >
            {t('components.addNew')}
          </button>
        </label>
      </div>

      {adding === 'maker' && (
        <AddManufacturer
          onCreated={(id) => {
            setManufacturerId(id);
            setAdding(null);
          }}
          onCancel={() => setAdding(null)}
        />
      )}
      {adding === 'package' && (
        <AddPackage
          onCreated={(id) => {
            setPackageId(id);
            setAdding(null);
          }}
          onCancel={() => setAdding(null)}
        />
      )}
      {adding === 'package-editor' && packageId && (() => {
        const selected = (packages.data ?? []).find((row) => row.id === packageId);
        return selected ? <PackageEditor package={selected} onClose={() => setAdding(null)} /> : null;
      })()}
      {adding === 'subtype' && typeDetail.data && (
        <AddSubtype
          parentId={typeDetail.data.id}
          parentName={pick(typeDetail.data.name_ru, typeDetail.data.name_en)}
          onCreated={(id) => {
            setTypeId(id);
            setAdding(null);
          }}
          onCancel={() => setAdding(null)}
        />
      )}

      <div className="component-form__row">
        <label>
          {t('components.package')}
          <select
            value={packageId}
            onChange={(event) => {
              const next = event.target.value;
              setPackageId(next);
              // Fill what the package determines — a 1206 chip resistor is a
              // quarter watt whoever made it. Only empty fields: a value
              // somebody typed is a value they meant, and a default that
              // overwrites it is worse than no default at all.
              const chosen = (packages.data ?? []).find((row) => row.id === next);
              const defaults = chosen?.default_attributes ?? {};
              if (Object.keys(defaults).length === 0) return;
              // A physical package may serve unlike parts. Its resistor power
              // rating, for example, is not a signal-diode parameter merely
              // because both can use the same body.
              const allowed = new Set(specs.map((spec) => spec.key));
              setAttributes((current) => {
                const merged = { ...current };
                for (const [key, fallback] of Object.entries(defaults)) {
                  if (allowed.has(key) && !merged[key]) merged[key] = fallback;
                }
                return merged;
              });
            }}
          >
            <option value="">—</option>
            {(packages.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.code}
                {row.size_mm ? ` · ${row.size_mm}` : ''}
              </option>
            ))}
          </select>
          {/* The short list is the point; the full catalogue is one click away
              and picking from it puts the entry on the short list for good. */}
          <span className="component-form__inline">
            <button
              type="button"
              className="component-form__scope"
              onClick={() => setPackageScope(packageScope === 'enabled' ? 'all' : 'enabled')}
            >
              {packageScope === 'enabled' ? t('components.showAll') : t('components.showShort')}
            </button>
            <button
              type="button"
              className="component-form__scope"
              onClick={() => setAdding(adding === 'package' ? null : 'package')}
            >
              {t('components.addNew')}
            </button>
            {packageId && (
              <button type="button" className="component-form__scope" onClick={() => setAdding('package-editor')}>
                {t('packages.edit')}
              </button>
            )}
          </span>
        </label>

        <label>
          {t('components.color')}
          <select value={colorId} onChange={(event) => setColorId(event.target.value)}>
            <option value="">—</option>
            {(colors.data ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {pick(row.name_ru, row.name_en)}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="component-form__scope"
            onClick={() => setColorScope(colorScope === 'enabled' ? 'all' : 'enabled')}
          >
            {colorScope === 'enabled' ? t('components.showAll') : t('components.showShort')}
          </button>
        </label>

        <label>
          {t('components.unit')}
          <select value={unitCode} onChange={(event) => setUnitCode(event.target.value)}>
            {(units.data ?? []).map((row) => (
              <option key={row.code} value={row.code}>
                {pick(row.name_ru, row.name_en)}
              </option>
            ))}
          </select>
        </label>

        {/* Both explanations are markers rather than lines of small text. They
            are read once, by the person meeting the field for the first time,
            and every field that carries two lines pushes the next field off the
            screen for everybody else. */}
        <label>
          <span className="component-form__label">
            {t('components.minStock')}
            <Hint text={t('components.minStockHint')} />
          </span>
          <input
            value={minStock}
            inputMode="decimal"
            onChange={(event) => setMinStock(event.target.value.replace(',', '.'))}
            placeholder="0"
          />
        </label>

        {/* Off by default and worth leaving off for anything counted in
            handfuls: turning it on means every movement of this part has to
            name the units it moved, one by one. That is right for a meter and
            absurd for a reel of resistors. */}
        <label className="component-form__check">
          <input
            type="checkbox"
            checked={trackSerials}
            onChange={(event) => setTrackSerials(event.target.checked)}
          />
          <span className="component-form__label">
            {t('components.trackSerials')}
            <Hint text={t('components.trackSerialsHint')} />
          </span>
        </label>
      </div>

      {specs.length > 0 && (
        <fieldset className="component-form__attributes">
          <legend>{t('components.parameters')}</legend>
          {specs.map((spec) => (
            <AttributeField
              key={spec.key}
              spec={spec}
              value={attributes[spec.key] ?? ''}
              onChange={(next) =>
                setAttributes((current) => ({ ...current, [spec.key]: next }))
              }
            />
          ))}
        </fieldset>
      )}

      <fieldset className="component-form__attributes component-form__measurement">
        <legend>{t('components.measurement.title')}</legend>
        {!measurementMode && source?.resolved_measurement_profile && (
          <p className="component-form__parsed">
            {t('components.measurement.inherited', {
              source: t(`components.measurement.source.${source.measurement_profile_source}`),
            })}
          </p>
        )}
        <div className="component-form__measurement-grid">
          <label>
            {t('components.measurement.method')}
            <select value={measurementMode} onChange={(event) => setMeasurementMode(event.target.value as MeasurementProfile['mode'] | '')}>
              <option value="">{t('components.measurement.none')}</option>
              <option value="mass">{t('components.measurement.mass')}</option>
              <option value="tape">{t('components.measurement.tape')}</option>
            </select>
          </label>
          {measurementMode && (
            <>
            <label>
              {t('components.measurement.sampleQuantity')}
              <input
                type="number"
                min={measurementMode === 'tape' ? 10 : 5}
                max="100"
                required
                value={sampleQuantity}
                onChange={(event) => setSampleQuantity(event.target.value)}
              />
            </label>
            <label>
              {t(measurementMode === 'tape' ? 'components.measurement.tapeSampleWeight' : 'components.measurement.sampleWeight')}
              <input inputMode="decimal" required value={sampleWeight} onChange={(event) => setSampleWeight(event.target.value.replace(',', '.'))} placeholder="0" />
            </label>
            <label>
              {t('components.measurement.carrierWeight')}
              <input inputMode="decimal" value={carrierWeight} onChange={(event) => setCarrierWeight(event.target.value.replace(',', '.'))} placeholder="0" />
            </label>
            {measurementMode === 'tape' && (
              <label className="component-form__measurement-tape-length">
                {t('components.measurement.tapeLength')}
                <input inputMode="decimal" required value={tapeLength} onChange={(event) => setTapeLength(event.target.value.replace(',', '.'))} placeholder="0" />
              </label>
            )}
            {packageId && (
              <div className="component-form__measurement-actions">
                <button
                  type="button"
                  className="component-form__scope"
                  disabled={!sampleWeight || (measurementMode === 'tape' && !tapeLength) || savePackageProfile.isPending}
                  onClick={() => savePackageProfile.mutate({
                    mode: measurementMode,
                    sample_quantity: Number(sampleQuantity),
                    sample_weight_g: sampleWeight,
                    carrier_weight_g: carrierWeight || '0',
                    tape_length_10_cells_mm: measurementMode === 'tape' ? tapeLength : null,
                  })}
                >
                  {t('components.measurement.saveAsPackage')}
                </button>
              </div>
            )}
            </>
          )}
        </div>
      </fieldset>

      <label className="component-form__name">
        {t('components.name')}
        <input
          value={name}
          onChange={(event) => {
            setName(event.target.value);
            setNameTouched(true);
          }}
          required
        />
        {!nameTouched && <span className="component-form__parsed">{t('components.nameAuto')}</span>}
        {nameTouched && (
          <button
            type="button"
            className="component-form__scope"
            onClick={() => {
              setNameTouched(false);
              if (report.data?.suggested_name) setName(report.data.suggested_name);
            }}
          >
            {t('components.nameReset')}
          </button>
        )}
      </label>

      <label>
        {t('components.description')}
        <textarea
          className="component-form__description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={10}
        />
        <span className="component-form__parsed">{t('common.markdownHint')}</span>
      </label>

      <label>
        {t('components.datasheet')}
        <input
          value={datasheetUrl}
          onChange={(event) => setDatasheetUrl(event.target.value)}
          placeholder="https://…"
        />
      </label>

      {report.data && report.data.candidates.length > 0 && (
        <DuplicateWarning report={report.data} onOpen={onOpenExisting} />
      )}

      {error && (
        <p className="panel__error">
          {t(`components.errors.${error}`, t('components.errors.unknown'))}
        </p>
      )}

      {actions}
    </form>
  );
}

/** Nested types are indented rather than nested further: optgroup does not nest,
 *  and a three-level select is worse than two spaces of indent. */
function TypeOptions({
  type,
  childrenOf,
  matchesBranch,
  depth,
}: {
  type: { id: string; code: string; name_ru: string; name_en: string };
  childrenOf: (id: string) => { id: string; code: string; name_ru: string; name_en: string }[];
  matchesBranch: (type: { id: string; code: string; name_ru: string; name_en: string }) => boolean;
  depth: number;
}) {
  const { pick } = useLabel();
  if (!matchesBranch(type)) return null;
  const children = childrenOf(type.id);
  return (
    <>
      <option value={type.id}>
        {INDENT.repeat(depth) + pick(type.name_ru, type.name_en)}
      </option>
      {children.map((child) => (
        <TypeOptions
          key={child.id}
          type={child}
          childrenOf={childrenOf}
          matchesBranch={matchesBranch}
          depth={depth + 1}
        />
      ))}
    </>
  );
}

function AttributeField({
  spec,
  value,
  onChange,
}: {
  spec: Attribute;
  value: string;
  onChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  const { pick } = useLabel();
  const label = pick(spec.label_ru, spec.label_en);

  return (
    <label className={spec.is_identity ? 'is-identity' : undefined}>
      <span>
        {label}
        {spec.unit && <em> ({spec.unit})</em>}
        {spec.is_required && <b aria-hidden="true"> *</b>}
        {/* Marked because these are the fields that decide whether the
            duplicate check can recognise this part at all. */}
        {spec.is_identity && (
          <i className="component-form__identity" title={t('components.identityHint')}>
            ◆
          </i>
        )}
      </span>

      {spec.data_type === 'enum' ? (
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">—</option>
          {(spec.enum_values ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {pick(option.label_ru, option.label_en)}
            </option>
          ))}
        </select>
      ) : spec.data_type === 'bool' ? (
        <input
          type="checkbox"
          checked={value === 'true'}
          onChange={(event) => onChange(event.target.checked ? 'true' : '')}
        />
      ) : (
        <input
          type="text"
          inputMode={spec.data_type === 'number' ? 'decimal' : undefined}
          value={value}
          // The server accepts either separator, but the field showed whatever
          // was typed, so there was no way to tell. Normalising here makes the
          // field show exactly what will be stored.
          onChange={(event) =>
            onChange(
              spec.data_type === 'number'
                ? event.target.value.replace(',', '.')
                : event.target.value,
            )
          }
        />
      )}
    </label>
  );
}
